import React from 'react';
import { formatCurrency } from '../../utils/currencyUtils';
import { getExpenseTheme } from './expenseGroupTheme';
import { Calendar, ChevronDown, ChevronUp, CreditCard, Pencil, Trash2, User, Wallet } from 'lucide-react';

const CARD_ACCENTS = {
  indigo: {
    avatar: 'from-indigo-600 to-slate-600',
    amount: 'text-indigo-700',
    badge: 'bg-indigo-100 text-indigo-800',
    edit: 'text-indigo-600 hover:bg-indigo-50',
  },
  violet: {
    avatar: 'from-violet-600 to-slate-600',
    amount: 'text-violet-700',
    badge: 'bg-violet-100 text-violet-800',
    edit: 'text-violet-600 hover:bg-violet-50',
  },
};

export default function ExpenseCard({
  expense,
  isExpanded,
  onToggle,
  onEdit,
  onDelete,
  onSettleFromWallet,
  companyCurrency,
  accent = 'indigo',
}) {
  const a = CARD_ACCENTS[accent] || CARD_ACCENTS.indigo;
  const title = expense.head || expense.employeeName || 'Expense';
  const initial = (title || 'E').charAt(0).toUpperCase();
  const groupLabel = expense.group || getExpenseTheme(accent === 'violet' ? 'variable' : 'fixed').label;

  return (
    <div className="rounded-xl border border-slate-200/90 bg-gradient-to-br from-slate-50/90 to-white shadow-sm overflow-hidden ring-1 ring-slate-200/50">
      <div
        className="p-3.5 cursor-pointer active:bg-slate-50/80 transition-colors"
        onClick={() => onToggle(expense.id)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && onToggle(expense.id)}
      >
        <div className="flex items-start gap-3">
          <div
            className={`w-11 h-11 rounded-full bg-gradient-to-br ${a.avatar} flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md`}
          >
            {initial}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-bold text-gray-900 truncate">{title}</h3>
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${a.badge}`}>
                {groupLabel}
              </span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ring-1 ring-inset ${
                  expense.settled
                    ? 'bg-emerald-100 text-emerald-800 ring-emerald-200'
                    : 'bg-red-100 text-red-800 ring-red-200'
                }`}
              >
                {expense.settled ? 'Settled' : 'Unsettled'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
              <span className="inline-flex items-center gap-1">
                <Calendar className="w-3 h-3 flex-shrink-0" aria-hidden />
                {expense.date || expense.month || 'No date'}
              </span>
              <span className="text-gray-300">·</span>
              <span className="inline-flex items-center gap-1">
                <CreditCard className="w-3 h-3 flex-shrink-0" aria-hidden />
                {expense.paymentMode || 'Cash'}
              </span>
              {expense.personName && (
                <>
                  <span className="text-gray-300">·</span>
                  <span className="inline-flex items-center gap-1 min-w-0">
                    <User className="w-3 h-3 flex-shrink-0" aria-hidden />
                    <span className="font-medium text-gray-700 truncate">{expense.personName}</span>
                  </span>
                </>
              )}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2 flex-shrink-0">
            <div className="text-right">
              <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Amount</p>
              <p className={`text-lg font-bold leading-tight tabular-nums ${a.amount}`}>
                {formatCurrency(Number(expense.amount) || 0, companyCurrency)}
              </p>
            </div>
            <div className="flex items-center gap-0.5">
              {!expense.settled && onSettleFromWallet && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSettleFromWallet(expense);
                  }}
                  className={`p-2 rounded-full transition-colors ${a.edit}`}
                  title="Settle from employee wallet"
                >
                  <Wallet className="w-4 h-4" aria-hidden />
                </button>
              )}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(expense);
                }}
                className={`p-2 rounded-full transition-colors ${a.edit}`}
                title="Edit"
              >
                <Pencil className="w-4 h-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(expense.id);
                }}
                className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" aria-hidden />
              </button>
              <span className="p-2 text-gray-400" aria-hidden>
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </span>
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-slate-100 p-4 bg-slate-50/60">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3">
              <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Date</span>
                  <span className="font-medium text-right">{expense.date || expense.month || '—'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Category</span>
                  <span className="font-medium text-right">{expense.head || '—'}</span>
                </div>
                {expense.personName && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-500">Person / vendor</span>
                    <span className="font-medium text-right">{expense.personName}</span>
                  </div>
                )}
                {expense.constantEntry && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-500">Constant entry</span>
                    <span className="font-medium text-right">{expense.constantEntry}</span>
                  </div>
                )}
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Payment mode</span>
                  <span className="font-medium text-right">{expense.paymentMode || 'Cash'}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Settlement</span>
                  <span
                    className={`font-semibold text-right ${expense.settled ? 'text-emerald-600' : 'text-red-600'}`}
                  >
                    {expense.settled ? 'Settled' : 'Unsettled'}
                  </span>
                </div>
                {expense.settledFromOtherWallet?.amount > 0 && (
                  <div className="rounded-lg bg-slate-50 border border-slate-100 px-2 py-2 text-xs text-slate-800">
                    <span className="font-semibold">From wallet:</span>{' '}
                    {expense.settledFromOtherWallet.sourceEmployeeName} —{' '}
                    {formatCurrency(Number(expense.settledFromOtherWallet.amount), companyCurrency)}
                  </div>
                )}
                {expense.settlementRemark && (
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-500">Settlement note</span>
                    <span className="font-medium text-right break-words max-w-[55%]">
                      {expense.settlementRemark}
                    </span>
                  </div>
                )}
                <div className="flex justify-between gap-2">
                  <span className="text-gray-500">Description</span>
                  <span className="font-medium text-right break-words max-w-[55%]">
                    {expense.description || '—'}
                  </span>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-3">
              <h4 className="text-[10px] font-bold text-gray-500 uppercase tracking-wide mb-2">Document</h4>
              <div className="text-sm text-gray-700">
                {(expense.receiptData || expense.receiptUrl) ? (
                  <a
                    href={expense.receiptData?.url || expense.receiptUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`font-medium underline ${a.edit.split(' ')[0]}`}
                  >
                    {expense.receiptData?.name || 'View document'}
                  </a>
                ) : (
                  <span className="text-gray-400">No receipt attached</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
